import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  VStack,
  Text,
  useColorModeValue,
  Textarea,
  Select,
  Wrap,
  Tag,
  TagLabel,
  TagCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  InputGroup,
  InputLeftElement,
  WrapItem,
  Tooltip,
  HStack
} from '@chakra-ui/react';
import { Article, MagnifyingGlass } from '@phosphor-icons/react';
import { getAllFeatures } from '../../firebase/features';
import { featureCategories } from '../../data/filterOptions';

export default function ArticleUpload({ onArticleSubmit, isDisabled, isMobile }) {
  const [articleUrl, setArticleUrl] = useState('');
  const [articleTitle, setArticleTitle] = useState('');
  const [articleDescription, setArticleDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Theme colors
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.800');
  const tagBg = useColorModeValue('gray.100', 'gray.700');
  const selectedTagBg = useColorModeValue('blue.100', 'blue.700');
  const tagColor = useColorModeValue('gray.800', 'white');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const categoryBg = useColorModeValue('gray.50', 'gray.700');
  
  // Load available features when component mounts
  useEffect(() => {
    const loadFeatures = async () => {
      setLoading(true);
      try {
        const result = await getAllFeatures();
        if (result.success) {
          setAvailableFeatures(result.data);
        }
      } catch (error) {
        console.error('Error loading features:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFeatures();
  }, []);

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!articleUrl.trim() || !articleTitle.trim()) {
      toast({
        title: "Error",
        description: "Please enter both article URL and title",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!isValidUrl(articleUrl)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid article URL",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create a standardized article object
      const articleData = {
        type: 'article',
        url: articleUrl,
        title: articleTitle,
        description: articleDescription,
        name: articleTitle.replace(/\s+/g, '-').toLowerCase() || `article-${Date.now()}`,
        path: articleUrl,
        predictions: selectedFeatures.map(featureId => ({
          class: availableFeatures[featureId]?.label || featureId,
          source: 'manual',
          confidence: 1
        }))
      };

      await onArticleSubmit(articleData);
      setArticleUrl('');
      setArticleTitle('');
      setArticleDescription('');
      setSelectedFeatures([]);
      
      toast({
        title: "Success",
        description: "Article link added successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error submitting article:', error);
      toast({
        title: "Error",
        description: "Failed to add article link",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeatureToggle = (featureId) => {
    if (selectedFeatures.includes(featureId)) {
      setSelectedFeatures(selectedFeatures.filter(id => id !== featureId));
    } else {
      setSelectedFeatures([...selectedFeatures, featureId]);
    }
  };

  // Format feature label for display
  const formatFeatureLabel = (label) => {
    return label
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Group features by category
  const getGroupedFeatures = () => {
    const groupedFeatures = {};
    
    Object.entries(featureCategories).forEach(([key, category]) => {
      const featuresInCategory = category.features
        .filter(featureId => {
          const feature = availableFeatures[featureId];
          if (!feature) return false;
          
          // Apply search filter if present
          if (searchQuery) {
            return feature.label.toLowerCase().includes(searchQuery.toLowerCase());
          }
          return true;
        })
        .map(featureId => {
          const feature = availableFeatures[featureId];
          return { id: featureId, ...feature };
        });

      if (featuresInCategory.length > 0) {
        groupedFeatures[key] = {
          label: category.label,
          features: featuresInCategory
        };
      }
    });
    
    return groupedFeatures;
  };

  const groupedFeatures = getGroupedFeatures();
  const hasFeatures = Object.keys(groupedFeatures).length > 0;

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <VStack spacing={isMobile ? 3 : 4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize={isMobile ? "sm" : "md"}>Article Title</FormLabel>
            <Input
              value={articleTitle}
              onChange={(e) => setArticleTitle(e.target.value)}
              placeholder="Enter article title"
              bg={inputBg}
              borderColor={borderColor}
              disabled={isDisabled}
              size={isMobile ? "sm" : "md"}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize={isMobile ? "sm" : "md"}>Article URL</FormLabel>
            <Input
              type="url"
              value={articleUrl}
              onChange={(e) => setArticleUrl(e.target.value)}
              placeholder="Enter article URL"
              bg={inputBg}
              borderColor={borderColor}
              disabled={isDisabled}
              size={isMobile ? "sm" : "md"}
            />
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize={isMobile ? "sm" : "md"}>Description (Optional)</FormLabel>
            <Textarea
              value={articleDescription}
              onChange={(e) => setArticleDescription(e.target.value)}
              placeholder="Enter a brief summary of the article"
              bg={inputBg}
              borderColor={borderColor}
              disabled={isDisabled}
              rows={isMobile ? 2 : 3}
              size={isMobile ? "sm" : "md"}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize={isMobile ? "sm" : "md"}>Features</FormLabel>
            
            {/* Search bar */}
            <InputGroup mb={isMobile ? 2 : 3} size={isMobile ? "sm" : "md"}>
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass opacity={0.5} />
              </InputLeftElement>
              <Input
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={inputBg}
                borderColor={borderColor}
                size={isMobile ? "sm" : "md"}
              />
            </InputGroup>
            
            {/* Selected features */}
            {selectedFeatures.length > 0 && (
              <Box mb={isMobile ? 2 : 3}>
                <Text fontSize={isMobile ? "xs" : "sm"} fontWeight="medium" mb={isMobile ? 1 : 2}>Selected Features:</Text>
                <Wrap spacing={isMobile ? 1 : 2}>
                  {selectedFeatures.map(featureId => (
                    <WrapItem key={featureId}>
                      <Tag
                        size={isMobile ? "sm" : "md"}
                        borderRadius="full"
                        variant="solid"
                        colorScheme="blue"
                      >
                        <TagLabel>{availableFeatures[featureId]?.label || featureId}</TagLabel>
                        <TagCloseButton onClick={() => handleFeatureToggle(featureId)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
            
            {/* Feature selection accordion */}
            {loading ? (
              <Text color="gray.500" fontSize={isMobile ? "xs" : "sm"}>Loading features...</Text>
            ) : !hasFeatures ? (
              <Text color="gray.500" fontSize={isMobile ? "xs" : "sm"}>No features found</Text>
            ) : (
              <Accordion allowMultiple defaultIndex={Object.keys(groupedFeatures)}>
                {Object.entries(groupedFeatures).map(([categoryKey, category]) => (
                  <AccordionItem key={categoryKey} mb={2} borderWidth="1px" borderRadius="md">
                    <AccordionButton py={isMobile ? 1 : 2} _hover={{ bg: categoryBg }}>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="medium" fontSize={isMobile ? "sm" : "md"}>{category.label}</Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {category.features.length} feature{category.features.length !== 1 ? 's' : ''}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={isMobile ? 2 : 3}>
                      <Wrap spacing={isMobile ? 1 : 2}>
                        {category.features.map((feature) => (
                          <WrapItem key={feature.id}>
                            <Tooltip label={feature.description} placement="top" hasArrow>
                              <Tag
                                size={isMobile ? "sm" : "md"}
                                borderRadius="full"
                                variant={selectedFeatures.includes(feature.id) ? "solid" : "subtle"}
                                bg={selectedFeatures.includes(feature.id) ? selectedTagBg : tagBg}
                                color={tagColor}
                                cursor="pointer"
                                onClick={() => handleFeatureToggle(feature.id)}
                                _hover={{
                                  transform: "translateY(-1px)",
                                  shadow: "sm",
                                  opacity: 0.8
                                }}
                                transition="all 0.2s"
                              >
                                <TagLabel fontSize={isMobile ? "xs" : "sm"}>{formatFeatureLabel(feature.label)}</TagLabel>
                              </Tag>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </FormControl>

          <Button
            type="submit"
            leftIcon={<Article weight="duotone" />}
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText="Adding article..."
            isDisabled={isDisabled}
            size={isMobile ? "sm" : "md"}
            width={isMobile ? "100%" : undefined} /* Full width on mobile */
          >
            Add Article
          </Button>
        </VStack>
      </form>
    </Box>
  );
}
